import { useCallback, useEffect, useRef, useState } from "react";

/** Slide-out transition length (ms) — must match the drawer's `duration-200` Tailwind class. */
export const DRAWER_ANIM_DURATION = 200;

/**
 * Manages the "slide out, then unmount" close sequence shared by the Org,
 * Person, and Role drawers: hide immediately (triggers the CSS transition)
 * and defer the actual `onClose` unmount until the transition finishes.
 */
export function useDrawerClose(onClose: () => void) {
  const [visible, setVisible] = useState(true);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleClose = useCallback(() => {
    setVisible(false);
    closeTimer.current = setTimeout(() => onClose(), DRAWER_ANIM_DURATION);
  }, [onClose]);

  /** Call from the "reset on new subject" effect so re-opening the drawer for a different record cancels any pending close. */
  const resetVisible = useCallback(() => {
    clearTimeout(closeTimer.current);
    setVisible(true);
  }, []);

  return { visible, handleClose, resetVisible };
}
